import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { submitForQC, preannotateJob, acceptSuggestion, rejectSuggestion, releaseJob } from '../../lib/callables';
import { validateAnnotations } from '../../lib/validator';
import { Annotation, GlobalCategory } from '../../types/labelSchema';
import { LabelingJob } from '../../types/labeling';
import AttributeForm from '../../components/AttributeForm';
import { useGlobalCategories } from '../../hooks/useGlobalCategories';

// Extended annotation type for AI suggestions
interface ExtendedAnnotation extends Annotation {
  id?: string;
  _assist?: boolean;
  _confidence?: number;
  _vmClass?: string;
}


interface BoundingBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  categoryId: string;
  attributes: Record<string, any>;
}

export default function Workbench() {
  const { jobId, submissionId } = useParams<{ jobId?: string; submissionId?: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Workbench — Pixano';
  }, []);
  
  const [job, setJob] = useState<LabelingJob | null>(null);
  const [submission, setSubmission] = useState<any>(null);
  const [taskData, setTaskData] = useState<any>(null);
  const [schemaData, setSchemaData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Canvas state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [boxes, setBoxes] = useState<BoundingBox[]>([]);
  const [selectedBox, setSelectedBox] = useState<BoundingBox | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('');
  
  // UI state
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showAttributes, setShowAttributes] = useState(false);
  const [notes, setNotes] = useState('');
  
  // Global objects state (using existing useGlobalCategories hook)
  
  // AI Assist state
  const [assistSuggestions, setAssistSuggestions] = useState<ExtendedAnnotation[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [task, setTask] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'attributes' | 'guide'>('attributes');
  const [wipCount, setWipCount] = useState(0);
  const [maxWip] = useState(3);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  
  // Global search state
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [selectedGlobalCategory, setSelectedGlobalCategory] = useState<GlobalCategory | null>(null);
  const { searchCategories, incrementUsage, getCategoryById } = useGlobalCategories();

  // Helper function to get category name from either schema or global categories
  const getCategoryName = (categoryId: string) => {
    // First try to find in schema classes
    const schemaCategory = schemaData?.classes?.find((c: any) => c.id === categoryId);
    if (schemaCategory) {
      return schemaCategory.label || schemaCategory.name || categoryId;
    }
    
    // Then try to find in global categories
    const globalCategory = getCategoryById(categoryId);
    if (globalCategory) {
      return globalCategory.displayName || globalCategory.name || categoryId;
    }
    
    // Fallback to categoryId
    return categoryId;
  };

  // Hotkey support for classification mode (moved before early returns)
  useEffect(() => {
    if (!job || !task) return;
    
    const taskKind = task?.kind || 'detection';
    const isClassification = taskKind === 'classification'
      || ((job?.labelSchemaSnapshot?.categories || []).every((c: any) => !c.shapes || c.shapes.length === 0));
    
    if (!isClassification || !job.labelSchemaSnapshot?.categories?.length) return;
    
    const handleKeyPress = (e: KeyboardEvent) => {
      const key = e.key;
      const numKey = parseInt(key);
      
      // Map keys 1-9 to categories
      if (numKey >= 1 && numKey <= 9 && numKey <= job.labelSchemaSnapshot.categories.length) {
        const category = job.labelSchemaSnapshot.categories[numKey - 1];
        if (category) {
          const next = [{ categoryId: category.id, attributes: {} }];
          setJob(prev => prev ? { ...prev, labelsDraft: next } : null);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [job, task, setJob]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        if (submissionId) {
          // Load submission-based workbench
          console.log(`🔍 Loading submission ${submissionId} for workbench`);
          
          const submissionDoc = await getDoc(doc(db, 'submissions', submissionId));
          if (!submissionDoc.exists()) {
            setError('Submission not found');
            return;
          }
          
          const submissionData = { id: submissionDoc.id, ...submissionDoc.data() } as any;
          setSubmission(submissionData);
          
          // Load task configuration
          const taskDoc = await getDoc(doc(db, 'tasks', submissionData.taskId));
          if (!taskDoc.exists()) {
            setError('Task not found');
            return;
          }
          
          const task = { id: taskDoc.id, ...taskDoc.data() } as any;
          setTaskData(task);
          
          // Load detection schema
          if (task.detection?.schemaRef?.id) {
            const schemaDoc = await getDoc(doc(db, 'labelSchemas', task.detection.schemaRef.id));
            if (schemaDoc.exists()) {
              const schema = { id: schemaDoc.id, ...schemaDoc.data() };
              setSchemaData(schema);
            }
          }
          
          // Load existing labels if any (from previous work)
          // For now, start with empty boxes
          setBoxes([]);
          
        } else if (jobId) {
          // Load job-based workbench (legacy)
          console.log(`🔍 Loading job ${jobId} for workbench`);
          
          const jobDoc = await getDoc(doc(db, 'labelingJobs', jobId));
          
          if (!jobDoc.exists()) {
            setError('Queue item not found');
            return;
          }
          
          const jobData = { id: jobDoc.id, ...jobDoc.data() } as LabelingJob;
          setJob(jobData);
          
          // Load task configuration
          const taskDoc = await getDoc(doc(db, 'tasks', jobData.taskId));
          if (taskDoc.exists()) {
            const task = { id: taskDoc.id, ...taskDoc.data() } as any;
            setTaskData(task);
          }
          
          // Load existing draft labels
          if (jobData.labelsDraft && jobData.labelsDraft.length > 0) {
            const existingBoxes = jobData.labelsDraft.map((label, index) => ({
              id: `box-${index}`,
              x: label.bbox[0],
              y: label.bbox[1],
              width: label.bbox[2] - label.bbox[0],
              height: label.bbox[3] - label.bbox[1],
              categoryId: label.categoryId,
              attributes: label.attributes || {}
            }));
            setBoxes(existingBoxes);
          }
          
          // Load AI suggestions if available
          if (jobData.assistSuggestions) {
            setAssistSuggestions(jobData.assistSuggestions);
          }
          
          // Auto-load suggestions if enabled and not already loaded
          if (taskDoc.exists()) {
            const taskData = taskDoc.data();
            if (taskData?.modelAssistEnabled && 
                taskData?.assistConfig?.enabled && 
                taskData?.assistConfig?.autoLoad && 
                !jobData.assistLoaded) {
              await loadSuggestions();
            }
          }
        } else {
          setError('No job ID or submission ID provided');
          return;
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data');
        setLoading(false);
      }
    };
    
    loadData();
  }, [jobId, submissionId]);

  // Load image after data is loaded
  useEffect(() => {
    if (!submission && !job) return;
    
    const loadImage = () => {
      // Get image URL from either job or submission
      const imageUrl = submissionId ? 
        (submission?.imageUrl || submission?.mediaUrl) : 
        job?.mediaUrl;
        
      console.log(`🔍 [Workbench] Loading image:`, {
        submissionId,
        jobId,
        imageUrl,
        submission: submission ? 'loaded' : 'not loaded',
        job: job ? 'loaded' : 'not loaded'
      });
        
      if (!imageUrl) {
        console.error('❌ [Workbench] No image URL found');
        setError('No image URL found');
        return;
      }
      
      const img = new Image();
      
      // Try different CORS settings
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        console.log(`✅ [Workbench] Image loaded successfully:`, {
          width: img.width,
          height: img.height,
          src: img.src
        });
        setImage(img);
        if (canvasRef.current) {
          const canvas = canvasRef.current;
          canvas.width = img.width;
          canvas.height = img.height;
          drawCanvas();
        }
      };
      
      img.onerror = (error) => {
        console.error('❌ [Workbench] Image load error:', error);
        console.error('❌ [Workbench] Failed to load image URL:', imageUrl);
        
        // Try without CORS as fallback
        console.log('🔄 [Workbench] Retrying without CORS...');
        const imgRetry = new Image();
        imgRetry.onload = () => {
          console.log(`✅ [Workbench] Image loaded successfully (retry):`, {
            width: imgRetry.width,
            height: imgRetry.height,
            src: imgRetry.src
          });
          setImage(imgRetry);
          if (canvasRef.current) {
            const canvas = canvasRef.current;
            canvas.width = imgRetry.width;
            canvas.height = imgRetry.height;
            drawCanvas();
          }
        };
        imgRetry.onerror = () => {
          console.error('❌ [Workbench] Image load failed even without CORS');
          setError(`Failed to load image: ${imageUrl}. This may be a CORS or permissions issue.`);
        };
        imgRetry.src = imageUrl;
      };
      
      console.log(`🔄 [Workbench] Setting image src: ${imageUrl}`);
      
      // Check if URL is valid
      try {
        new URL(imageUrl);
        img.src = imageUrl;
      } catch (urlError) {
        console.error('❌ [Workbench] Invalid image URL:', imageUrl);
        setError(`Invalid image URL: ${imageUrl}`);
      }
    };
    
    loadImage();
  }, [submission, job, submissionId]);


  const drawCanvas = () => {
    if (!canvasRef.current || !image) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw image
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    
    // Draw AI suggestions (dashed) if enabled
    if (showSuggestions && assistSuggestions.length > 0) {
      assistSuggestions.forEach((suggestion, index) => {
        const [x, y, x2, y2] = suggestion.bbox;
        const width = x2 - x;
        const height = y2 - y;
        
        // Set dashed line style for suggestions
        ctx.setLineDash([5, 5]);
        ctx.strokeStyle = '#10b981'; // Green for AI suggestions
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, width, height);
        
        // Draw AI label
        ctx.fillStyle = '#10b981';
        ctx.font = '12px Arial';
        ctx.fillText(`AI: ${suggestion.categoryId}`, x, y - 5);
        
        // Draw confidence
        if (suggestion._confidence) {
          ctx.fillText(`${Math.round(suggestion._confidence * 100)}%`, x + width - 30, y + 15);
        }
      });
    }
    
    // Draw user boxes (solid)
    ctx.setLineDash([]); // Reset to solid lines
    boxes.forEach((box, index) => {
      ctx.strokeStyle = selectedBox?.id === box.id ? '#ef4444' : '#3b82f6';
      ctx.lineWidth = selectedBox?.id === box.id ? 3 : 2;
      ctx.strokeRect(box.x, box.y, box.width, box.height);
      
      // Draw category label
      ctx.fillStyle = selectedBox?.id === box.id ? '#ef4444' : '#3b82f6';
      ctx.font = '12px Arial';
      const categoryName = getCategoryName(box.categoryId);
      ctx.fillText(categoryName, box.x, box.y - 5);
      
      // Draw box number
      ctx.fillText(`${index + 1}`, box.x + 5, box.y + 15);
    });
  };

  useEffect(() => {
    drawCanvas();
  }, [boxes, selectedBox, image, showSuggestions, assistSuggestions]);

  // Load WIP count when job loads
  useEffect(() => {
    if (job?.assigneeId) {
      loadWipCount();
    }
  }, [job?.assigneeId]);

  // Hotkeys
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // 1..9 select categories by palette order
      if (e.key >= '1' && e.key <= '9') {
        const idx = Number(e.key) - 1;
        const id = job?.labelSchemaSnapshot.ui?.paletteOrder?.[idx];
        if (id) setActiveCategory(id);
      }
      if (e.key === 'Escape') {
        setIsDrawing(false);
        setStartPoint(null);
        setSelectedBox(null);
      }
      if (e.key === 'Backspace' && selectedBox) {
        handleDeleteBox(selectedBox.id);
      }
      if (e.key === 'Enter') {
        saveDraft();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [job, selectedBox]);

  // Debounced autosave of drafts
  useEffect(() => {
    if (!job || boxes.length === 0) return;
    
    const t = setTimeout(() => {
      const annotations = boxes.map(box => ({
        id: box.id,
        categoryId: box.categoryId,
        bbox: [box.x, box.y, box.x + box.width, box.y + box.height],
        attributes: box.attributes
      }));
      
      updateDoc(doc(db, 'labelingJobs', job.id), {
        labelsDraft: annotations,
        lastActionBy: 'client',
        lastActionAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }, 500); // 500ms debounce
    
    return () => clearTimeout(t);
  }, [boxes, job]);

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Check if clicking on existing box
    const clickedBox = boxes.find(box => 
      x >= box.x && x <= box.x + box.width &&
      y >= box.y && y <= box.y + box.height
    );
    
    if (clickedBox) {
      setSelectedBox(clickedBox);
      setSelectedCategory(clickedBox.categoryId);
      setShowAttributes(true);
      return;
    }
    
    // Start drawing new box
    if (selectedCategory) {
      setIsDrawing(true);
      setStartPoint({ x, y });
      setSelectedBox(null);
      setShowAttributes(false);
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Draw preview box
    drawCanvas();
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(
        Math.min(startPoint.x, x),
        Math.min(startPoint.y, y),
        Math.abs(x - startPoint.x),
        Math.abs(y - startPoint.y)
      );
      ctx.setLineDash([]);
    }
  };

  const handleCanvasMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const newBox: BoundingBox = {
      id: `box-${Date.now()}`,
      x: Math.min(startPoint.x, x),
      y: Math.min(startPoint.y, y),
      width: Math.abs(x - startPoint.x),
      height: Math.abs(y - startPoint.y),
      categoryId: selectedCategory,
      attributes: {}
    };
    
    // Only add box if it has minimum size
    if (newBox.width > 10 && newBox.height > 10) {
      setBoxes(prev => [...prev, newBox]);
      setSelectedBox(newBox);
      setShowAttributes(true);
    }
    
    setIsDrawing(false);
    setStartPoint(null);
  };

  const handleDeleteBox = (boxId: string) => {
    setBoxes(prev => prev.filter(box => box.id !== boxId));
    if (selectedBox?.id === boxId) {
      setSelectedBox(null);
      setShowAttributes(false);
    }
  };

  const handleUpdateBoxAttributes = (boxId: string, attributes: Record<string, any>) => {
    setBoxes(prev => prev.map(box => 
      box.id === boxId ? { ...box, attributes } : box
    ));
    if (selectedBox?.id === boxId) {
      setSelectedBox(prev => prev ? { ...prev, attributes } : null);
    }
  };

  const saveDraft = async () => {
    if (!job) return;
    
    try {
      setSaving(true);
      const annotations = convertBoxesToAnnotations();
      await updateDoc(doc(db, 'labelingJobs', job.id), {
        labelsDraft: annotations,
        lastActionBy: 'client',
        lastActionAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      console.log('Draft saved successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setSaving(false);
    }
  };

  const convertBoxesToAnnotations = (): Annotation[] => {
    return boxes.map(box => ({
      bbox: [box.x, box.y, box.x + box.width, box.y + box.height] as [number, number, number, number],
      categoryId: box.categoryId,
      attributes: box.attributes
    }));
  };

  // const handleSaveDraft = async () => {
  //   if (!job) return;
  //   
  //   try {
  //     setSaving(true);
  //     const annotations = convertBoxesToAnnotations();
  //     
  //     await updateDoc(doc(db, 'labelingJobs', job.id), {
  //       labelsDraft: annotations,
  //       updatedAt: serverTimestamp()
  //     });
  //     
  //     alert('Draft saved successfully!');
  //   } catch (err) {
  //     console.error('Error saving draft:', err);
  //     alert('Failed to save draft');
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  const handleSubmitForQC = async () => {
    if (!job) return;
    
    try {
      setSubmitting(true);
      
      // For classification mode, use labelsDraft directly (no bbox)
      // For detection mode, convert boxes to annotations
      const annotations = isClassification 
        ? (job.labelsDraft || [])
        : convertBoxesToAnnotations();
      
      // Client-side validation
      const validation = validateAnnotations(job.labelSchemaSnapshot, annotations);
      if (!validation.ok) {
        alert(`Validation failed: ${validation.errors[0] || 'Fix validation errors'}`);
        return;
      }
      
      await submitForQC(job.id, annotations);
      alert('Submitted for QC review successfully!');
      navigate('/labeling/queue');
    } catch (err) {
      console.error('Error submitting for QC:', err);
      alert('Failed to submit for QC. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // AI Assist functions
  const loadSuggestions = async () => {
    if (!job || !task?.modelAssistEnabled) return;
    
    try {
      setLoadingSuggestions(true);
      const result = await preannotateJob(job.id);
      setAssistSuggestions(job.assistSuggestions || []);
      alert(result.message);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      alert('Failed to load AI suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAcceptSuggestion = async (suggestionId: string) => {
    if (!job) return;
    
    try {
      await acceptSuggestion(job.id, suggestionId);
      // Refresh job data to get updated suggestions and draft
      const jobDoc = await getDoc(doc(db, 'labelingJobs', job.id));
      if (jobDoc.exists()) {
        const jobData = { id: jobDoc.id, ...jobDoc.data() } as LabelingJob;
        setJob(jobData);
        setAssistSuggestions(jobData.assistSuggestions || []);
        
        // Update boxes with new draft
        if (jobData.labelsDraft && jobData.labelsDraft.length > 0) {
          const existingBoxes = jobData.labelsDraft.map((label, index) => ({
            id: `box-${index}`,
            x: label.bbox[0],
            y: label.bbox[1],
            width: label.bbox[2] - label.bbox[0],
            height: label.bbox[3] - label.bbox[1],
            categoryId: label.categoryId,
            attributes: label.attributes || {}
          }));
          setBoxes(existingBoxes);
        }
      }
    } catch (error) {
      console.error('Error accepting suggestion:', error);
      alert('Failed to accept suggestion');
    }
  };

  const handleRejectSuggestion = async (suggestionId: string) => {
    if (!job) return;
    
    try {
      await rejectSuggestion(job.id, suggestionId);
      // Remove from local state
      setAssistSuggestions(prev => prev.filter(s => s.id !== suggestionId));
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
      alert('Failed to reject suggestion');
    }
  };

  const loadWipCount = async () => {
    try {
      // Query for current user's WIP jobs
      const wipQuery = query(
        collection(db, 'labelingJobs'),
        where('assigneeId', '==', job?.assigneeId),
        where('status', 'in', ['in_progress', 'returned_to_annotator'])
      );
      const wipSnapshot = await getDocs(wipQuery);
      setWipCount(wipSnapshot.size);
    } catch (error) {
      console.error('Error loading WIP count:', error);
    }
  };

  const handleReleaseJob = async (reason: string) => {
    try {
      await releaseJob(jobId!, reason);
      setShowReleaseModal(false);
      navigate('/labeling/queue');
    } catch (error) {
      console.error('Error releasing job:', error);
      alert('Error releasing queue item. Please try again.');
    }
  };

  const handleAddGlobalCategory = (globalCategory: GlobalCategory) => {
    setSelectedGlobalCategory(globalCategory);
    setActiveCategory(globalCategory.id);
    setSelectedCategory(globalCategory.id);
    setShowGlobalSearch(false);
    setGlobalSearchTerm('');
    
    // Increment usage count
    incrementUsage(globalCategory.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading workbench...</p>
        </div>
      </div>
    );
  }

  if (error || (!job && !submission)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Item not found'}</p>
          <div className="space-x-2">
            <button
              onClick={() => navigate('/labeling/queue')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Back to Queue
            </button>
            <button
              onClick={() => {
                const imageUrl = submissionId ? 
                  (submission?.imageUrl || submission?.mediaUrl) : 
                  job?.mediaUrl;
                if (imageUrl) {
                  window.open(imageUrl, '_blank');
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Test Image URL
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get schema and categories from either job or schemaData
  const schema = job?.labelSchemaSnapshot || schemaData;
  const categories = schema?.categories || schema?.classes || [];
  
  // Determine mode: classification vs detection
  const taskKind = taskData?.kind || 'detection';
  // fallback: infer from schema if task.kind missing
  const isClassification = taskKind === 'classification'
    || ((schema?.categories || schema?.classes || []).every((c: any) => !c.shapes || c.shapes.length === 0));


  // ClassPicker component for classification mode
  function ClassPicker({ categories, value, onChange }: {
    categories: any[], value?: string, onChange: (id: string) => void
  }) {
    return (
      <div className="grid grid-cols-2 gap-2">
        {categories.map((c: any, idx: number) => (
          <button key={c.id}
            className={`border rounded p-2 text-left ${value === c.id ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => onChange(c.id)}>
            <div className="text-sm font-medium">{c.label}</div>
            <div className="text-xs opacity-60">[{idx + 1}]</div>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Detection Queue - Labeling</h1>
            <p className="text-sm text-gray-600">
              {job ? `Queue Item ${job.id.slice(-8)}` : `Submission ${submission?.id.slice(-8) || 'Unknown'}`}
            </p>

            {(taskData || submission) && (
              <div className="mt-2 text-sm text-gray-700 flex flex-wrap gap-x-4 gap-y-1">
                {(taskData?.title || submission?.taskTitle) && (
                  <span>
                    <span className="font-semibold">Task:</span>{' '}
                    {taskData?.title || submission?.taskTitle}
                  </span>
                )}
                {(taskData?.category || submission?.category) && (
                  <span>
                    <span className="font-semibold">Category:</span>{' '}
                    {taskData?.category || submission?.category}
                  </span>
                )}
                {(taskData?.subcategory || submission?.subcategory) && (
                  <span>
                    <span className="font-semibold">Subcategory:</span>{' '}
                    {taskData?.subcategory || submission?.subcategory}
                  </span>
                )}
                {(taskData?.itemType || submission?.itemType) && (
                  <span>
                    <span className="font-semibold">Item Type:</span>{' '}
                    {taskData?.itemType || submission?.itemType}
                  </span>
                )}
                {submission?.contentLanguageLabel && (
                  <span>
                    <span className="font-semibold">Language:</span>{' '}
                    {submission.contentLanguageLabel}
                  </span>
                )}
              </div>
            )}
            
            {/* Context Chips */}
            <div className="flex items-center space-x-2 mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                Task: {job?.taskId || submission?.taskId || 'Unknown'}
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                Type: Detection
              </span>
              {/* Classification chip if submission has classification */}
              {(job?.submissionId && job?.classification) && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                  Class: {job.classification.classId}
                </span>
              )}
              {submission?.contentLanguageLabel && (
                <span className="text-sm text-gray-600">
                  Language: {submission.contentLanguageLabel}
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-4 mt-1">
              <span className="text-xs text-gray-500">
                My WIP: {wipCount}/{maxWip}
              </span>
              {job?.priority && (
                <span className={`px-2 py-1 text-xs rounded-full ${
                  job.priority >= 4 ? 'bg-red-100 text-red-800' :
                  job.priority >= 3 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  Priority {job.priority}
                </span>
              )}
              {job?.slaBreached && (
                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                  SLA Breached
                </span>
              )}
              {job?.slaWarning && !job?.slaBreached && (
                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                  SLA Warning
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* AI Assist Controls */}
            {task?.modelAssistEnabled && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={loadSuggestions}
                  disabled={loadingSuggestions || job?.assistLoaded}
                  className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  {loadingSuggestions ? 'Loading...' : job?.assistLoaded ? 'Loaded' : 'Load AI'}
                </button>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={showSuggestions}
                    onChange={(e) => setShowSuggestions(e.target.checked)}
                    className="mr-1"
                  />
                  Show AI
                </label>
              </div>
            )}
            
            <div className="flex space-x-2">
              <button
                onClick={() => setShowReleaseModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Release Item
              </button>
              <button
                onClick={saveDraft}
                disabled={saving}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={handleSubmitForQC}
                disabled={submitting || (isClassification ? (job?.labelsDraft || []).length === 0 : boxes.length === 0)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit for QC'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Left Rail - Category Palette */}
        <div className="w-64 bg-white border-r border-gray-200 p-4">
          <h3 className="font-semibold mb-4">Standard Categories</h3>
          <div className="space-y-2">
            {categories.map((category: any, index: number) => {
              const hotkey = job?.labelSchemaSnapshot.ui?.hotkeys?.[category.id];
              const isActive = activeCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setActiveCategory(category.id);
                    setSelectedGlobalCategory(null); // Clear global selection
                  }}
                  className={`w-full text-left px-3 py-2 rounded border ${
                    isActive
                      ? 'bg-blue-100 border-blue-500 text-blue-700'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{category.label}</div>
                    {hotkey && (
                      <span className="text-xs bg-gray-200 px-1 rounded">{hotkey}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {category.shapes.join(', ')} • {category.attributes?.length || 0} attributes
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Global Search Section */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm">Global Search</h4>
              <button
                onClick={() => setShowGlobalSearch(!showGlobalSearch)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                {showGlobalSearch ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {showGlobalSearch && (
              <div className="space-y-3">
                <div>
                  <input
                    type="text"
                    value={globalSearchTerm}
                    onChange={(e) => setGlobalSearchTerm(e.target.value)}
                    placeholder="Search categories..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {searchCategories(globalSearchTerm, 10).map((globalCategory) => (
                    <button
                      key={globalCategory.id}
                      onClick={() => handleAddGlobalCategory(globalCategory)}
                      className={`w-full text-left px-2 py-1 text-sm rounded border ${
                        selectedGlobalCategory?.id === globalCategory.id
                          ? 'bg-green-100 border-green-500 text-green-700'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{globalCategory.name}</span>
                        {globalCategory.usageCount && globalCategory.usageCount > 0 && (
                          <span className="text-xs text-gray-500">
                            {globalCategory.usageCount}
                          </span>
                        )}
                      </div>
                      {globalCategory.description && (
                        <div className="text-xs text-gray-500 truncate">
                          {globalCategory.description}
                        </div>
                      )}
                    </button>
                  ))}
                  
                  {searchCategories(globalSearchTerm, 10).length === 0 && globalSearchTerm && (
                    <div className="text-xs text-gray-500 text-center py-2">
                      No categories found for "{globalSearchTerm}"
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {selectedCategory && (
            <div className="mt-6">
              <h4 className="font-medium mb-2">Instructions</h4>
              <p className="text-sm text-gray-600">
                Click and drag on the image to draw a bounding box for {
                  selectedGlobalCategory 
                    ? selectedGlobalCategory.name
                    : categories.find((c: any) => c.id === selectedCategory)?.label
                }.
              </p>
              {selectedGlobalCategory && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                  <strong>Global Category:</strong> This category is available across all tasks.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Center - Canvas (hidden for classification) */}
        {!isClassification && (
          <div className="flex-1 bg-gray-100 p-4">
            <div className="bg-white rounded-lg shadow-sm p-4 h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Image Annotation</h3>
              <div className="text-sm text-gray-600">
                {boxes.length} box{boxes.length !== 1 ? 'es' : ''} • {selectedCategory ? `Selected: ${
                  selectedGlobalCategory 
                    ? selectedGlobalCategory.name
                    : categories.find((c: any) => c.id === selectedCategory)?.label
                }` : 'Select a category'}
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <canvas
                ref={canvasRef}
                className="max-w-full max-h-full cursor-crosshair"
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                style={{ maxHeight: 'calc(100vh - 200px)' }}
              />
            </div>
          </div>
        </div>
        )}

        {/* Right Rail - Attributes, Guide & Notes */}
        <div className="w-80 bg-white border-l border-gray-200 p-4">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 mb-4">
            <button
              onClick={() => setActiveTab('attributes')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'attributes'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Attributes
            </button>
            <button
              onClick={() => setActiveTab('guide')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'guide'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Guide
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'attributes' && (
            <>
              {isClassification ? (
                <div>
                  <h3 className="font-semibold mb-4">Classification</h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Category
                    </label>
                    <ClassPicker 
                      categories={categories} 
                      value={(job?.labelsDraft || [])[0]?.categoryId}
                      onChange={(id) => {
                        const next = [{ categoryId: id, attributes: {} }]; // no bbox
                        setJob(prev => prev ? { ...prev, labelsDraft: next } : null);
                      }}
                    />
                  </div>
                  
                  {/* Show attributes for selected category */}
                  {(() => {
                    const currentClass = (job?.labelsDraft || [])[0]?.categoryId;
                    const category = categories.find((c: any) => c.id === currentClass);
                    return category?.attributes ? (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Attributes
                        </label>
                        <AttributeForm
                          attrs={category.attributes}
                          value={(job?.labelsDraft || [])[0]?.attributes || {}}
                          onChange={(next) => {
                            const currentDraft = job?.labelsDraft || [];
                            const updatedDraft = currentDraft.length > 0 
                              ? [{ ...currentDraft[0], attributes: next }]
                              : [{ categoryId: currentClass, attributes: next }];
                            setJob(prev => prev ? { ...prev, labelsDraft: updatedDraft } : null);
                          }}
                        />
                      </div>
                    ) : null;
                  })()}
                </div>
              ) : selectedBox && showAttributes ? (
                <div>
                  <h3 className="font-semibold mb-4">Box Attributes</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <div className="text-sm text-gray-900">
                        {categories.find((c: any) => c.id === selectedBox.categoryId)?.label}
                      </div>
                    </div>
                    
                    {(() => {
                      const category = categories.find((c: any) => c.id === selectedBox.categoryId);
                      return category?.attributes ? (
                        <AttributeForm
                          attrs={category.attributes}
                          value={selectedBox.attributes || {}}
                          onChange={(next) => handleUpdateBoxAttributes(selectedBox.id, next)}
                        />
                      ) : null;
                    })()}
                    
                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={() => handleDeleteBox(selectedBox.id)}
                        className="w-full px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Delete Box
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="font-semibold mb-4">Notes</h3>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full h-32 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add notes about this annotation..."
                  />
                  
                  <div className="mt-6">
                    <h4 className="font-medium mb-2">Schema Info</h4>
                    <div className="text-sm text-gray-600">
                      <p><strong>Schema:</strong> {schema.name}</p>
                      <p><strong>Version:</strong> {schema._ref?.version}</p>
                      <p><strong>Categories:</strong> {categories.length}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === 'guide' && (
            <div>
              <h3 className="font-semibold mb-4">Labeling Guide</h3>
              {job?.labelSchemaSnapshot?.guide ? (
                <div className="space-y-4">
                  {job.labelSchemaSnapshot.guide
                    .filter((g: any) => !activeCategory || g.categoryId === activeCategory)
                    .map((guide: any, index: number) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">{guide.title}</h4>
                        <p className="text-sm text-gray-600 mb-3">{guide.text}</p>
                        {guide.examples && guide.examples.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2">Examples:</p>
                            <div className="flex flex-wrap gap-2">
                              {guide.examples.map((example: string, idx: number) => (
                                <img
                                  key={idx}
                                  src={example}
                                  alt={`Example ${idx + 1}`}
                                  className="w-16 h-16 object-cover rounded border"
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  {job.labelSchemaSnapshot.guide.filter((g: any) => !activeCategory || g.categoryId === activeCategory).length === 0 && (
                    <p className="text-sm text-gray-500">No guide available for the selected category.</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No labeling guide available for this task.</p>
              )}
            </div>
          )}
          
          {/* AI Suggestions Panel */}
          {task?.modelAssistEnabled && assistSuggestions.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-medium mb-3">AI Suggestions ({assistSuggestions.length})</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {assistSuggestions.map((suggestion) => {
                  const category = categories.find((c: any) => c.id === suggestion.categoryId);
                  return (
                    <div key={suggestion.id} className="bg-green-50 border border-green-200 rounded p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-green-800">
                          {category?.label || suggestion.categoryId}
                        </span>
                        <span className="text-xs text-green-600">
                          {suggestion._confidence ? `${Math.round(suggestion._confidence * 100)}%` : 'N/A'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        Box: [{suggestion.bbox.map(Math.round).join(', ')}]
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => suggestion.id && handleAcceptSuggestion(suggestion.id)}
                          className="flex-1 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => suggestion.id && handleRejectSuggestion(suggestion.id)}
                          className="flex-1 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Release Job Modal */}
      {showReleaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Release Job</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to release this job back to the queue? Please provide a reason.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleReleaseJob(e.target.value);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a reason...</option>
                  <option value="Blocked">Blocked</option>
                  <option value="Out of scope">Out of scope</option>
                  <option value="Too complex">Too complex</option>
                  <option value="Technical issues">Technical issues</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="flex space-x-2 pt-4">
                <button
                  onClick={() => setShowReleaseModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}